// The Monarch navigation tree, lifted verbatim out of Dashboard.jsx.
//
// MOVE ONLY — every entry, every gate and every comment is byte-identical to
// what was inline.
//
// Why it moved: this is ~115 lines of pure configuration living inside a
// render-heavy component, and Dashboard.jsx has hit its ratchet ceiling three
// times in one day. Each time the fix was to shave a line somewhere else,
// which buys room without decomposing anything — exactly what the ratchet
// exists to prevent. Nav config is the obvious thing that was never render
// logic in the first place.
//
// Every dependency is an explicit parameter rather than a closure capture, so
// adding a nav entry can never again accidentally depend on Dashboard state.

/**
 * @param {object} ctx
 *   user                — the signed-in user (for window.EmpireRoles.isOwner)
 *   hasCapability       — (cap) => boolean, already bound to that user
 *   isOwner, isManager  — resolved role booleans
 *   canEditTasks, canEnterCashCount — feature gates
 */
export function buildNavGroups({
  user, hasCapability, isOwner, isManager,
  canEditTasks, canEnterCashCount,
}) {
  return [
    {
      id: 'home', label: 'Home', icon: '⌂',
      children: [
        ...(window.EmpireRoles.isOwner(user) ? [{ id: 'home-birdseye', label: 'Birdseye' }] : []),
        ...(isManager ? [{ id: 'manager-todo', label: 'My to-do list' }] : []),
        // Staff get their own named destination (CJ 2026-08-10). Managers keep
        // the manager list above; this one is the shift-level list — what to
        // read, whether you are clocked in, what is left before you go. Named
        // rather than only embedded in the task page, because a thing with no
        // name in the menu is a thing nobody finds.
        ...(isManager ? [] : [{ id: 'my-todo', label: 'My to-do list' }]),
        { id: 'home-daily',     label: 'Daily' },
        // Manager-only: This Week + Year. Staff just see Daily — those
        // longer reports are management surfaces.
        ...(isManager ? [{ id: 'home-this-week', label: 'This Week' }] : []),
        ...(isManager ? [{ id: 'home-year',      label: 'Year' }] : []),
      ],
    },
    {
      id: 'schedule', label: 'Schedule', icon: '▦',
      children: [
        // CJ feedback 2026-05-09: My Availability + Time Off moved to
        // Profile (PTO balance + request flow live there). Schedule
        // stays focused on shifts, swaps, and time card; managers also
        // get Availability Health + Build Schedule.
        { id: 'sched-mine',  label: 'My Schedule' },
        { id: 'sched-swaps', label: 'Shift Swaps' },
        { id: 'sched-time',  label: 'Time Card' },
        { id: 'settings-holidays', label: 'Holidays' },
        ...(isManager ? [{ id: 'sched-timeoff', label: 'Time Off Approvals' }] : []),
        ...(isManager ? [{ id: 'sched-ops',   label: 'Availability Health' }] : []),
        ...(isManager ? [{ id: 'sched-build', label: 'Build Schedule' }] : []),
      ],
    },
    {
      id: 'tasks', label: 'Tasks', icon: '☑',
      children: [
        { id: 'today',     label: 'Task list' },
        { id: 'yesterday', label: 'Completed tasks' },
        // CJ 2026-07-29: Incident Report moved here (was under Operations),
        // placed after Completed tasks. Stays universal so any staff member can
        // file one, even though Operations itself is now manager+ only.
        { id: 'report-incident', label: 'Incident Report' },
        // D247: reporting is universal — an opener who walks into an undone close
        // is exactly who needs it. The manager queue is separate and gated.
        { id: 'report-handoff', label: 'Report a handoff issue' },
        ...(isManager ? [{ id: 'reports-handoff', label: 'Handoff reports' }] : []),
        ...(canEditTasks ? [{ id: 'settings-tasks',     label: 'Task editor' }] : []),
        // D249. Managers and owners only — deliberately isManager rather than
        // canEditTasks, which includes shift leads: someone editing the standard
        // they are about to be measured against is the wrong shape.
        ...(isManager ? [{ id: 'settings-shift-standards', label: 'End-of-shift standards' }] : []),
        ...(isManager ? [{ id: 'tasks-performance',     label: 'Missed tasks' }] : []),
        ...(isManager ? [{ id: 'tasks-disputes',        label: 'Disputes' }] : []),
      ],
    },
    // Operations: Equipment / Temperatures / Maintenance / Compliance.
    // CJ 2026-07-29: the WHOLE group is manager+ only now — hidden from baristas
    // and shift leads (Equipment / Temperatures / Maintenance included, by CJ's
    // call). Compliance tightened to owners only. Incident Report moved to the
    // Tasks group, where it stays universal.
    ...(isManager ? [{
      id: 'operations', label: 'Operations', icon: '🔧',
      children: [
        { id: 'equipment',        label: 'Equipment' },
        { id: 'ops-temperatures', label: 'Temperatures' },
        { id: 'maintenance',      label: 'Maintenance' },
        { id: 'ops-pis',          label: 'Pis' }, // store Pi health (empire-kds-label D296)
        { id: 'ops-store-check',  label: 'Store Check' }, // a still of every store + what the AI flagged (cameras D358)
        ...(isOwner ? [{ id: 'compliance', label: 'Compliance' }] : []),
      ],
    }] : []),
    // Tally (2026-07-12): inventory/ordering/COGS app, lead tier only.
    // Launch view — the app itself opens in a new tab via signed handoff.
    ...(hasCapability('use_tally') ? [{ id: 'tally', label: 'Tally', icon: '/assets/tally-icon.png' }] : []),
    // Roast Curve (Docket #45, 2026-07-24): coffee QC + roasting education —
    // owner/manager/roaster only for now. Same launch shape as Tally: the app
    // itself opens in a new tab via signed handoff.
    ...(hasCapability('use_roastcurve') ? [{ id: 'roastcurve', label: 'Roast Curve', icon: '🔥' }] : []),
    {
      id: 'knowledgehub', label: 'Knowledge', icon: '📖',
      children: [
        // KB integration 2026-07-09: Library + Ask are the empire-kb service
        // via the /api/kb proxy. Ask-first (CJ 2026-07-11): Ask leads, the
        // library is the fallback. CJ 2026-07-29: retired the separate
        // "Policies & SOPs" and "Suggestions" menu items; Library is the single
        // knowledge landing. Calculators renamed to TDS Calculator.
        // CJ 2026-10-13: Ask Empire is for managers and owners only; everyone
        // else lands on the Library.
        ...(hasCapability('use_ask_empire') ? [{ id: 'kh-ask', label: 'Ask Empire' }] : []),
        { id: 'kh-library',     label: 'Library' },
        { id: 'kh-recipes',     label: 'Recipes' },
        { id: 'kh-tools',       label: 'TDS Calculator' },
        ...(isManager ? [{ id: 'kh-gaps', label: 'Gap Report' }] : []),
      ],
    },
    { id: 'board', label: 'Board', icon: '📌' },
    { id: 'team', label: 'Team', icon: '👥' },
    ...(isManager ? [{ id: 'hiring', label: 'Hiring', icon: '📥' }] : []),
    // Chat deprecated 2026-07-29 (CJ): Square is the system of record for chat.
    // Replaced by The Board (2026-07-31); ChatView kept read-only/archived.
    // { id: 'chat', label: 'Chat', icon: '💬' },
    ...((isManager || canEnterCashCount) ? [{
      id: 'reports', label: 'Reports', icon: '📊',
      children: [
        ...(isOwner ? [{ id: 'reports-pnl', label: 'P&L' }] : []),
        ...(isManager ? [
          { id: 'reports-week-close', label: 'Week close' },
          { id: 'reports-payroll',   label: 'Payroll' },
          { id: 'reports-incidents', label: 'Incidents' },
          { id: 'reports-stock',     label: 'Stock audit' },
          { id: 'settings-audit',    label: 'Audit log' },
        ] : []),
        ...(canEnterCashCount ? [{ id: 'reports-cash-counts', label: 'Cash counts' }] : []),
      ],
    }] : []),
    // Settings hidden from baristas entirely (systems review 2026-07-22 —
    // their only child was read-only Holidays, which now lives under
    // Schedule where the mental model puts it).
    ...(isManager ? [{
      id: 'admin', label: 'Settings', icon: '⚙',
      children: [
        ...(isManager ? [{ id: 'settings-locations',       label: 'Locations' }] : []),
        ...(isManager ? [{ id: 'settings-hiring',           label: 'Hiring' }] : []),
        ...(isManager ? [{ id: 'settings-events',           label: 'Events' }] : []),
        ...(isOwner ? [{ id: 'settings-payroll-roster',    label: 'Pay & position' }] : []),
        ...(isOwner ? [{ id: 'settings-admin-toggles',     label: 'Admin toggles' }] : []),
      ],
    }] : []),
  ];
}
